import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Copy,
    CheckCheck,
    Share2,
    MessageCircle,
    Twitter,
    Linkedin,
    Mail,
    Gift,
    AlertCircle,
    Sparkles,
    BookmarkCheck,
} from "lucide-react";
import { Button } from "../shared/Button";
import { getShareStatus, patchEmailOnResponse } from "../../lib/api";
import { isValidEmail } from "../../lib/validation";

function buildShareUrl(shareCode) {
    const origin =
        typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/?ref=${shareCode}`;
}

function buildStatusUrl(shareCode) {
    const origin =
        typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/r/${shareCode}`;
}

const SHARE_MESSAGE =
    "I just took this 4-minute survey from Klareet. It's shaping a new career clarity tool. Worth a look:";

function encodedLink(link) {
    return encodeURIComponent(`${SHARE_MESSAGE} ${link}`);
}

/**
 * Share & Earn surface used on Glimpse and on /r/:code (status).
 *
 * variant === "glimpse"  → render the prominent "Save your dashboard" card.
 *                          Hide the legacy amber email-capture form.
 * variant === "status"   → reverse: hide the dashboard card; show the amber
 *                          "Add your email" notice when email_on_file=false.
 */
export function ShareEarn({
    shareCode,
    responseId,
    initialEmailOnFile,
    variant = "status",
}) {
    const shareUrl = useMemo(() => buildShareUrl(shareCode), [shareCode]);
    const statusUrl = useMemo(() => buildStatusUrl(shareCode), [shareCode]);

    const [status, setStatus] = useState(null);
    const [copied, setCopied] = useState(false);
    const [showEmailForm, setShowEmailForm] = useState(!initialEmailOnFile);
    const [emailDraft, setEmailDraft] = useState("");
    const [emailSaving, setEmailSaving] = useState(false);
    const [emailSaved, setEmailSaved] = useState(initialEmailOnFile ?? false);
    const [emailError, setEmailError] = useState(null);
    const copyTimer = useRef(null);

    useEffect(() => {
        let alive = true;
        getShareStatus(shareCode).then((res) => {
            if (alive && res.ok) setStatus(res.data);
        });
        return () => {
            alive = false;
        };
    }, [shareCode]);

    const count = status?.referral_count ?? 0;
    const goal = status?.goal ?? 20;
    const pct = Math.min(100, Math.round((count / goal) * 100));
    const earned = Boolean(status?.free_year_earned);

    function handleCopy() {
        navigator.clipboard?.writeText(shareUrl).then(() => {
            setCopied(true);
            if (copyTimer.current) clearTimeout(copyTimer.current);
            copyTimer.current = setTimeout(() => setCopied(false), 2000);
        });
    }

    async function saveEmail(e) {
        e?.preventDefault?.();
        setEmailError(null);
        if (!isValidEmail(emailDraft) || !emailDraft) {
            setEmailError("That email looks off.");
            return;
        }
        setEmailSaving(true);
        const res = await patchEmailOnResponse({
            responseId,
            email: emailDraft.trim(),
            shareCode,
        });
        setEmailSaving(false);
        if (res.ok) {
            setEmailSaved(true);
            setShowEmailForm(false);
            setStatus((prev) =>
                prev ? { ...prev, email_on_file: true } : prev,
            );
        } else {
            setEmailError(
                typeof res.error === "string"
                    ? res.error
                    : "We couldn't save that. Try again.",
            );
        }
    }

    return (
        <section
            className="rounded-3xl border border-klareet-T/40 bg-gradient-to-br from-klareet-CARD to-[#0B1120] p-8 md:p-10 relative overflow-hidden"
            data-testid="share-earn"
        >
            <div
                className="absolute -top-28 right-0 w-64 h-64 rounded-full blur-3xl bg-klareet-T/20 pointer-events-none"
                aria-hidden
            />
            <div className="flex items-start gap-3 mb-6">
                <span className="p-2 rounded-xl bg-klareet-T/15">
                    <Gift className="text-klareet-T2" size={22} />
                </span>
                <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] font-mono text-klareet-T2">
                        Share & earn
                    </div>
                    <h2 className="mt-1 text-2xl md:text-3xl font-semibold tracking-tight text-klareet-TEXT">
                        1 year of Klareet free when 20 friends respond
                    </h2>
                    <p className="mt-2 text-klareet-MUT text-sm md:text-base max-w-prose leading-relaxed">
                        No catch. We want honest voices. When 20 people
                        complete this survey through your link, we give you a
                        full year of Klareet free at launch.
                    </p>
                </div>
            </div>

            {/* Save your dashboard card — Glimpse variant only */}
            {variant === "glimpse" && responseId ? (
                <DashboardSaveCard
                    responseId={responseId}
                    shareCode={shareCode}
                    initialStatus={status}
                    onSaved={(updated) => {
                        setStatus((prev) => ({ ...(prev ?? {}), ...updated }));
                        setEmailSaved(true);
                    }}
                />
            ) : null}

            {/* Link + copy */}
            <div className="mt-6 rounded-2xl border border-klareet-bor bg-klareet-card/60 p-3 flex flex-col md:flex-row items-stretch md:items-center gap-3">
                <div className="flex-1 font-mono text-[13px] md:text-sm text-klareet-TEXT/90 bg-transparent px-3 py-2 overflow-x-auto whitespace-nowrap">
                    {shareUrl}
                </div>
                <Button
                    variant={copied ? "outline" : "primary"}
                    size="sm"
                    onClick={handleCopy}
                    data-testid="copy-share-link-btn"
                >
                    {copied ? (
                        <>
                            <CheckCheck size={16} />
                            Copied
                        </>
                    ) : (
                        <>
                            <Copy size={16} />
                            Copy
                        </>
                    )}
                </Button>
            </div>

            {/* Share row */}
            <div className="mt-5">
                <div className="text-[11px] uppercase tracking-[0.2em] font-mono text-klareet-MUT mb-3">
                    Share via
                </div>
                <div className="flex flex-wrap gap-2">
                    <ShareButton
                        data-testid="share-whatsapp-btn"
                        href={`https://wa.me/?text=${encodedLink(shareUrl)}`}
                        label="WhatsApp"
                        icon={
                            <MessageCircle
                                size={18}
                                className="text-[#25D366]"
                            />
                        }
                    />
                    <ShareButton
                        data-testid="share-twitter-btn"
                        href={`https://twitter.com/intent/tweet?text=${encodedLink(shareUrl)}`}
                        label="X / Twitter"
                        icon={<Twitter size={18} className="text-klareet-TEXT" />}
                    />
                    <ShareButton
                        data-testid="share-linkedin-btn"
                        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                        label="LinkedIn"
                        icon={<Linkedin size={18} className="text-[#0A66C2]" />}
                    />
                    <ShareButton
                        data-testid="share-email-btn"
                        href={`mailto:?subject=${encodeURIComponent("Quick thing. 4-minute survey")}&body=${encodedLink(shareUrl)}`}
                        label="Email"
                        icon={<Mail size={18} className="text-klareet-T2" />}
                    />
                    <a
                        href={statusUrl}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-klareet-bor bg-transparent text-klareet-MUT hover:text-klareet-TEXT hover:border-klareet-T text-sm transition-colors"
                        data-testid="view-status-link"
                    >
                        <Share2 size={16} />
                        Track progress
                    </a>
                </div>
            </div>

            {/* Progress */}
            <div className="mt-7">
                <div className="flex items-center justify-between text-sm text-klareet-MUT mb-2">
                    <span
                        className="font-mono text-klareet-TEXT"
                        data-testid="referral-progress-text"
                    >
                        {count} of {goal} friends have responded
                    </span>
                    <span className="font-mono text-klareet-MUT">{pct}%</span>
                </div>
                <div className="h-3 rounded-full bg-klareet-BOR overflow-hidden">
                    <motion.div
                        className="h-full bg-gradient-to-r from-klareet-T to-klareet-T2 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{
                            duration: 0.9,
                            ease: [0.2, 0.8, 0.2, 1],
                        }}
                    />
                </div>
            </div>

            {/* Earned banner */}
            <AnimatePresence>
                {earned ? (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-6 p-4 rounded-2xl border border-klareet-GREEN/50 bg-klareet-GREEN/10 text-klareet-GREEN flex items-center gap-3"
                        data-testid="earned-banner"
                    >
                        <Sparkles size={20} />
                        <span className="font-medium">
                            You've earned 1 year of Klareet free. We'll reach
                            out at launch.
                        </span>
                    </motion.div>
                ) : null}
            </AnimatePresence>

            {/* Legacy amber email capture — Status variant only (bookmark recovery flow) */}
            {variant === "status" &&
            responseId &&
            !emailSaved &&
            !status?.email_on_file &&
            showEmailForm ? (
                <motion.form
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    onSubmit={saveEmail}
                    className="mt-6 p-4 rounded-2xl border border-klareet-AMBER/40 bg-klareet-AMBER/5"
                    data-testid="email-capture-form"
                >
                    <div className="flex items-start gap-3">
                        <AlertCircle
                            size={20}
                            className="text-klareet-AMBER shrink-0 mt-0.5"
                        />
                        <div className="flex-1">
                            <div className="text-klareet-TEXT font-medium">
                                Add your email so we can deliver the reward
                            </div>
                            <div className="text-sm text-klareet-MUT mt-1">
                                Only used to contact you if you hit 20
                                responses.
                            </div>
                            <div className="mt-3 flex flex-col sm:flex-row gap-2">
                                <input
                                    type="email"
                                    autoComplete="email"
                                    inputMode="email"
                                    placeholder="you@example.com"
                                    value={emailDraft}
                                    onChange={(e) =>
                                        setEmailDraft(e.target.value)
                                    }
                                    className="flex-1 px-4 py-3 rounded-xl border border-klareet-bor bg-klareet-card text-klareet-TEXT placeholder:text-klareet-DIM outline-none focus:border-klareet-T focus:ring-2 focus:ring-klareet-T/40 transition-colors"
                                    data-testid="email-capture-input"
                                />
                                <Button
                                    size="md"
                                    onClick={saveEmail}
                                    disabled={emailSaving}
                                    data-testid="email-capture-submit"
                                >
                                    {emailSaving ? "Saving…" : "Add email"}
                                </Button>
                            </div>
                            {emailError ? (
                                <div className="mt-2 text-sm text-klareet-RED font-mono">
                                    {emailError}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </motion.form>
            ) : null}
        </section>
    );
}

function DashboardSaveCard({ responseId, shareCode, initialStatus, onSaved }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [touchedEmail, setTouchedEmail] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [justSaved, setJustSaved] = useState(false);

    const status = initialStatus ?? {};
    const emailOnFile = Boolean(status.email_on_file);
    const nameOnFile = Boolean(status.name_on_file);
    const isSaved = emailOnFile;

    const emailInvalid =
        touchedEmail && email.length > 0 && !isValidEmail(email);

    const trackUrl = `/r/${shareCode}`;

    async function save(e) {
        e?.preventDefault?.();
        setError(null);
        const trimmedEmail = email.trim();
        const trimmedName = name.trim();
        if (!trimmedEmail && !trimmedName) {
            setError(
                "Add at least your name or email so we can save your dashboard.",
            );
            return;
        }
        if (trimmedEmail && !isValidEmail(trimmedEmail)) {
            setError("That email looks off.");
            return;
        }
        setSaving(true);
        const res = await patchEmailOnResponse({
            responseId,
            shareCode,
            email: trimmedEmail || undefined,
            name: trimmedName || undefined,
        });
        setSaving(false);
        if (res.ok) {
            setJustSaved(true);
            onSaved?.({
                email_on_file: trimmedEmail
                    ? true
                    : (initialStatus?.email_on_file ?? false),
                name_on_file: trimmedName
                    ? true
                    : (initialStatus?.name_on_file ?? false),
            });
        } else {
            setError(
                typeof res.error === "string"
                    ? res.error
                    : "We couldn't save that. Try again.",
            );
        }
    }

    return (
        <div
            className="mt-1 mb-6 p-5 md:p-6 rounded-2xl border border-klareet-T/35 bg-klareet-NAVY/40"
            data-testid="dashboard-save-card"
        >
            <div className="flex items-start gap-3">
                <span className="p-2 rounded-xl bg-klareet-T/15 shrink-0">
                    <BookmarkCheck className="text-klareet-T2" size={20} />
                </span>
                <div className="flex-1 min-w-0">
                    <div className="text-klareet-TEXT text-base md:text-lg font-semibold tracking-tight">
                        {isSaved
                            ? "Your dashboard is saved"
                            : "Save your dashboard"}
                    </div>
                    <p className="mt-1 text-sm text-klareet-MUT max-w-prose leading-relaxed">
                        Add your name and email so you can come back to this
                        dashboard anytime. We'll only contact you about your
                        rewards or the Klareet launch.
                    </p>

                    <form
                        onSubmit={save}
                        className="mt-4 grid grid-cols-1 gap-2"
                    >
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value.slice(0, 80))}
                            placeholder={
                                nameOnFile
                                    ? "Update your name"
                                    : "Your name (optional)"
                            }
                            maxLength={80}
                            className="px-4 py-3 rounded-xl border border-klareet-bor bg-klareet-card text-klareet-TEXT placeholder:text-klareet-DIM outline-none focus:border-klareet-T focus:ring-2 focus:ring-klareet-T/40 transition-colors"
                            data-testid="dashboard-name-input"
                            autoComplete="name"
                        />
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onBlur={() => setTouchedEmail(true)}
                            placeholder={
                                emailOnFile
                                    ? "Update your email"
                                    : "Your email (recommended)"
                            }
                            autoComplete="email"
                            inputMode="email"
                            className={`px-4 py-3 rounded-xl border bg-klareet-card text-klareet-TEXT placeholder:text-klareet-DIM outline-none transition-colors ${
                                emailInvalid
                                    ? "border-klareet-RED focus:border-klareet-RED focus:ring-2 focus:ring-klareet-RED/40"
                                    : "border-klareet-bor focus:border-klareet-T focus:ring-2 focus:ring-klareet-T/40"
                            }`}
                            data-testid="dashboard-email-input"
                        />
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-1">
                            <Button
                                size="md"
                                onClick={save}
                                disabled={saving}
                                data-testid="dashboard-save-btn"
                            >
                                {saving
                                    ? "Saving…"
                                    : isSaved
                                      ? "Update"
                                      : "Save my dashboard"}
                            </Button>
                            {justSaved ? (
                                <motion.div
                                    initial={{ opacity: 0, x: -6 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="text-sm text-klareet-GREEN font-mono"
                                    data-testid="dashboard-save-confirmation"
                                >
                                    Saved. Bookmark{" "}
                                    <a
                                        href={trackUrl}
                                        className="underline underline-offset-4 hover:text-klareet-TEXT"
                                    >
                                        {trackUrl}
                                    </a>{" "}
                                    now.
                                </motion.div>
                            ) : null}
                        </div>
                        {error ? (
                            <div
                                className="mt-1 text-sm text-klareet-RED font-mono"
                                data-testid="dashboard-save-error"
                            >
                                {error}
                            </div>
                        ) : null}
                    </form>
                </div>
            </div>
        </div>
    );
}

function ShareButton({ href, icon, label, ...rest }) {
    return (
        <motion.a
            href={href}
            target="_blank"
            rel="noreferrer noopener"
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.97 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-klareet-bor bg-klareet-card hover:border-klareet-T transition-colors text-sm text-klareet-TEXT"
            {...rest}
        >
            {icon}
            {label}
        </motion.a>
    );
}
